import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faCheck,
    faArrowRight,
    faShieldAlt,
    faChartBar,
    faBolt,
    faStar,
    faLock,
    faQrcode,
    faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { HashLink as Link } from 'react-router-hash-link';
import { Helmet } from 'react-helmet';
import smoothscroll from 'smoothscroll-polyfill';

import api from './utils/api';
import { trackEvent } from './utils/analytics';

const THEMES = {
    geometric: {
        id: 'geometric',
        name: 'Geometric Zigzag',
        bg: '#cfdae3',
        cardBg: '#ffffff',
        textColor: '#111111',
        accentColor: '#946f00',
        buttonColor: '#946f00',
        buttonRadius: '0px',
        fontHeading: "'Epilogue', sans-serif",
        fontBody: "'Oswald', sans-serif",
        tag: 'Bold & Creative',
    },
    simple: {
        id: 'simple',
        name: 'Minimal Clean',
        bg: '#fff9f5',
        cardBg: '#ffffff',
        textColor: '#0b0e11',
        accentColor: '#ceac03',
        buttonColor: '#0b0e11',
        buttonRadius: '50px',
        fontHeading: "'Epilogue', sans-serif",
        fontBody: "'Roboto', sans-serif",
        tag: 'Clean & Timeless',
    },
    bw: {
        id: 'bw',
        name: 'Black & White',
        bg: '#111111',
        cardBg: '#1e1e1e',
        textColor: '#ffffff',
        accentColor: '#ffffff',
        buttonColor: '#ffffff',
        buttonRadius: '0px',
        fontHeading: "'Montserrat', sans-serif",
        fontBody: "'Courier Prime', monospace",
        tag: 'Monochrome Editorial',
    },
    colors: {
        id: 'colors',
        name: 'Three Colors',
        bg: '#f4ede6',
        cardBg: '#ffffff',
        textColor: '#1a1b1b',
        accentColor: '#e05915',
        buttonColor: '#e05915',
        buttonRadius: '50px',
        fontHeading: "'Epilogue', sans-serif",
        fontBody: "'Lora', serif",
        tag: 'Warm & Vibrant',
    },
    image: {
        id: 'image',
        name: 'Editorial Image',
        bg: '#f0f3f6',
        cardBg: '#ffffff',
        textColor: '#1a1b1b',
        accentColor: '#799da8',
        buttonColor: '#1a1b1b',
        buttonRadius: '4px',
        fontHeading: "'Abril Fatface', cursive",
        fontBody: "'Raleway', sans-serif",
        tag: 'Visual & Sophisticated',
    },
};

const FAQ_ITEMS = [
    {
        q: 'Are responses truly 100% anonymous?',
        a: 'Yes, completely. Feedbee does not log IP addresses, fingerprint devices, or require respondents to sign up. By removing the fear of social discomfort or awkwardness, you get authentic, unfiltered answers.',
    },
    {
        q: 'Do respondents need to create an account or install an app?',
        a: 'Never. Anyone with your link or QR code can immediately open the page in any mobile or desktop browser and submit feedback in under 60 seconds.',
    },
    {
        q: 'What types of questions can I ask?',
        a: 'Feedbee supports open-ended text questions, 1-to-5 numerical rating scales, and quick yes/no questions. You can mix and match any question format on a single page.',
    },
    {
        q: 'Can I export or share the feedback results?',
        a: 'Yes. Every feedback page generates an interactive visual dashboard in your account, an optional shareable public link for your team or audience, and a one-click downloadable PDF report.',
    },
    {
        q: 'How does the free tier work?',
        a: 'You can create an account and start collecting feedback right away for free with no credit card required. If you need unlimited pages and advanced customization, affordable Pro plans are available.',
    },
];

class Home extends Component {
    constructor(props) {
        super(props);
        this.state = {
            email: '',
            checkbox: false,
            emailSubmitted: false,
            error: '',
            activeTheme: 'simple',
            sampleRating: 5,
            sampleYesNo: 'yes',
            openFaq: 0,
        };
    }

    componentDidMount() {
        smoothscroll.polyfill();
    }

    render() {
        const DOMAIN = `${process.env.DOMAIN || 'https://www.feedbee.co'}`;
        const canonical = `${DOMAIN}`;
        const theme = THEMES[this.state.activeTheme];

        return (
            <main className="container landing-revamp">
                <Helmet>
                    <title>feedbee | Beautiful Feedback Forms, 100% Candid Responses</title>
                    <link rel="canonical" href={canonical} />
                    <meta
                        name="description"
                        content="Create customized, stylish feedback pages in minutes. Gather 100% candid, anonymous peer, client, and audience feedback with visual charts and instant PDF reports."
                    />
                </Helmet>

                {/* 1. HERO SECTION */}
                <section className="hero-revamp" id="start">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <span className="badge-pulse"></span>
                            <span>Smart, Anonymous & Stylish Feedback</span>
                        </div>

                        <h1 className="hero-title">
                            Beautiful feedback forms. <br />
                            <span className="hero-title-accent">100% candid responses.</span>
                        </h1>

                        <p className="hero-subtitle">
                            Say goodbye to sterile Google forms and awkward review meetings. Feedbee lets you create
                            custom-designed, anonymous feedback pages in under 2 minutes—so your team, clients,
                            or audience can tell you the honest truth.
                        </p>

                        <div className="hero-cta-group">
                            <Link to="/auth/sign-up" onClick={() => trackEvent('cta_clicked', { location: 'hero_primary' })}>
                                <button className="btn-primary-hero">
                                    Start using for free <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '10px' }} />
                                </button>
                            </Link>

                            <a
                                href="/f/feedback-JQt6CIr1c"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary-hero"
                                onClick={() => trackEvent('cta_clicked', { location: 'hero_sample_results' })}
                            >
                                See Live Sample Report ↗
                            </a>
                        </div>

                        <div className="hero-trust-bar">
                            <div className="trust-item">
                                <FontAwesomeIcon icon={faShieldAlt} className="trust-icon" />
                                <span>100% Anonymous</span>
                            </div>
                            <span className="trust-dot">•</span>
                            <div className="trust-item">
                                <FontAwesomeIcon icon={faBolt} className="trust-icon" />
                                <span>Zero Signup for Respondents</span>
                            </div>
                            <span className="trust-dot">•</span>
                            <div className="trust-item">
                                <FontAwesomeIcon icon={faChartBar} className="trust-icon" />
                                <span>Instant Visual Reports</span>
                            </div>
                        </div>
                    </div>

                    {/* HERO PREVIEW CARD MOCKUP */}
                    <div className="hero-visual">
                        <div className="mockup-frame">
                            <div className="mockup-header-dots">
                                <span className="dot red"></span>
                                <span className="dot yellow"></span>
                                <span className="dot green"></span>
                                <span className="mockup-url-pill">feedbee.co/p/sarah-design-q3</span>
                            </div>

                            <div className="mockup-card-body">
                                <div className="card-top-tag">
                                    <FontAwesomeIcon icon={faLock} style={{ marginRight: '6px', fontSize: '11px' }} />
                                    Anonymous Response Mode
                                </div>
                                <h3 className="mockup-form-title">Quarterly Peer Feedback</h3>
                                <p className="mockup-form-desc">
                                    Help me improve my collaboration, communication, and design delivery.
                                </p>

                                <div className="mockup-question">
                                    <div className="question-label">1. How clear was our communication during sprints?</div>
                                    <div className="rating-pill-row">
                                        {[1, 2, 3, 4, 5].map((num) => (
                                            <div
                                                key={num}
                                                className={`rating-pill ${num === 5 ? 'active' : ''}`}
                                            >
                                                {num}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mockup-question">
                                    <div className="question-label">2. What is one thing I should continue doing?</div>
                                    <div className="mockup-input-field">
                                        "Sharing wireframe drafts early—it saved our dev team hours this sprint!"
                                    </div>
                                </div>

                                <div className="mockup-submit-btn">
                                    Submit Anonymous Feedback
                                </div>
                            </div>

                            {/* Floating Analytics Pill */}
                            <div className="floating-stat-card">
                                <div className="stat-icon-wrap">
                                    <FontAwesomeIcon icon={faStar} />
                                </div>
                                <div className="stat-text-wrap">
                                    <div className="stat-value">4.9 / 5.0 Average</div>
                                    <div className="stat-sub">14 candid responses received</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 2. INTERACTIVE TEMPLATE SHOWCASE */}
                <section className="preview-section" id="preview">
                    <div className="section-head text-center">
                        <div className="section-tag">SAY GOODBYE TO BORING FORMS</div>
                        <h2 className="section-title">5 aesthetic themes designed to impress.</h2>
                        <p className="section-desc">
                            Your questions deserve better than sterile spreadsheets. Choose a style that matches your
                            personality, studio, or workshop. Click a theme below to preview it live:
                        </p>
                    </div>

                    {/* Theme Switcher Tabs */}
                    <div className="theme-tab-bar">
                        {Object.keys(THEMES).map((key) => {
                            const t = THEMES[key];
                            const isActive = this.state.activeTheme === key;
                            return (
                                <button
                                    key={key}
                                    className={`theme-tab-btn ${isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        this.setState({ activeTheme: key });
                                        trackEvent('theme_preview_switched', { theme: key });
                                    }}
                                >
                                    <span className="theme-color-chip" style={{ backgroundColor: t.accentColor }}></span>
                                    {t.name}
                                </button>
                            );
                        })}
                    </div>

                    {/* Dynamic Theme Demo Container */}
                    <div className="live-demo-viewport" style={{ backgroundColor: theme.bg }}>
                        <div
                            className="dynamic-form-card"
                            style={{
                                backgroundColor: theme.cardBg,
                                color: theme.textColor,
                                border: this.state.activeTheme === 'bw' ? '1px solid #444' : '1px solid rgba(0,0,0,0.06)',
                            }}
                        >
                            <div className="dynamic-card-header">
                                <div
                                    className="dynamic-tag"
                                    style={{
                                        color: theme.accentColor,
                                        fontFamily: theme.fontBody,
                                    }}
                                >
                                    {theme.tag} • 100% ANONYMOUS
                                </div>
                                <h3
                                    className="dynamic-title"
                                    style={{
                                        fontFamily: theme.fontHeading,
                                        color: theme.textColor,
                                    }}
                                >
                                    Client Project Debrief
                                </h3>
                                <p
                                    className="dynamic-desc"
                                    style={{
                                        fontFamily: theme.fontBody,
                                        color: this.state.activeTheme === 'bw' ? '#aaa' : '#555',
                                    }}
                                >
                                    Please share your candid thoughts on our recent product launch.
                                </p>
                            </div>

                            {/* Interactive Question 1: Rating */}
                            <div className="dynamic-question-box">
                                <label
                                    className="dynamic-q-label"
                                    style={{ fontFamily: theme.fontHeading, color: theme.textColor }}
                                >
                                    How would you rate the overall quality of delivery?
                                </label>
                                <div className="dynamic-rating-group">
                                    {[1, 2, 3, 4, 5].map((score) => {
                                        const isSelected = this.state.sampleRating === score;
                                        return (
                                            <button
                                                key={score}
                                                type="button"
                                                onClick={() => this.setState({ sampleRating: score })}
                                                className={`dynamic-score-btn ${isSelected ? 'selected' : ''}`}
                                                style={{
                                                    borderRadius: theme.buttonRadius,
                                                    fontFamily: theme.fontBody,
                                                    backgroundColor: isSelected ? theme.buttonColor : 'transparent',
                                                    color: isSelected ? (this.state.activeTheme === 'bw' ? '#000' : '#fff') : theme.textColor,
                                                    borderColor: theme.buttonColor,
                                                }}
                                            >
                                                {score}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Interactive Question 2: Yes/No */}
                            <div className="dynamic-question-box">
                                <label
                                    className="dynamic-q-label"
                                    style={{ fontFamily: theme.fontHeading, color: theme.textColor }}
                                >
                                    Would you recommend our services to a partner?
                                </label>
                                <div className="dynamic-yesno-group">
                                    {['yes', 'no'].map((choice) => {
                                        const isSelected = this.state.sampleYesNo === choice;
                                        return (
                                            <button
                                                key={choice}
                                                type="button"
                                                onClick={() => this.setState({ sampleYesNo: choice })}
                                                className={`dynamic-yesno-btn ${isSelected ? 'selected' : ''}`}
                                                style={{
                                                    borderRadius: theme.buttonRadius,
                                                    fontFamily: theme.fontBody,
                                                    backgroundColor: isSelected ? theme.buttonColor : 'transparent',
                                                    color: isSelected ? (this.state.activeTheme === 'bw' ? '#000' : '#fff') : theme.textColor,
                                                    borderColor: theme.buttonColor,
                                                }}
                                            >
                                                {choice === 'yes' ? 'YES' : 'NO'}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Question 3: Open Text */}
                            <div className="dynamic-question-box">
                                <label
                                    className="dynamic-q-label"
                                    style={{ fontFamily: theme.fontHeading, color: theme.textColor }}
                                >
                                    What exceeded your expectations the most?
                                </label>
                                <textarea
                                    rows="2"
                                    readOnly
                                    value="The attention to detail and proactive communication made this the smoothest rollout of our quarter."
                                    className="dynamic-textarea"
                                    style={{
                                        fontFamily: theme.fontBody,
                                        backgroundColor: this.state.activeTheme === 'bw' ? '#292929' : '#f9f9f9',
                                        color: theme.textColor,
                                        borderRadius: theme.buttonRadius === '50px' ? '12px' : theme.buttonRadius,
                                    }}
                                />
                            </div>

                            {/* Demo Submit Button */}
                            <button
                                type="button"
                                className="dynamic-submit-btn"
                                style={{
                                    backgroundColor: theme.buttonColor,
                                    color: this.state.activeTheme === 'bw' ? '#000' : '#fff',
                                    borderRadius: theme.buttonRadius,
                                    fontFamily: theme.fontBody,
                                }}
                            >
                                Send Anonymous Feedback
                            </button>
                        </div>
                    </div>
                </section>

                {/* 3. HOW IT WORKS */}
                <section className="steps-section" id="how-it-works">
                    <div className="section-head text-center">
                        <div className="section-tag">SIMPLE WORKFLOW</div>
                        <h2 className="section-title">From idea to candid feedback in 3 steps</h2>
                        <p className="section-desc">
                            No complicated software setup or lengthy employee onboarding. Create, share, and learn.
                        </p>
                    </div>

                    <div className="steps-grid">
                        <div className="step-card">
                            <div className="step-number">01</div>
                            <div className="step-icon-wrap">
                                <FontAwesomeIcon icon={faBolt} />
                            </div>
                            <h3 className="step-title">Craft Your Questions</h3>
                            <p className="step-text">
                                Pick from 5 signature themes and customize with your questions. Combine 1–5 rating scales,
                                yes/no checks, and open text prompts.
                            </p>
                        </div>

                        <div className="step-card">
                            <div className="step-number">02</div>
                            <div className="step-icon-wrap">
                                <FontAwesomeIcon icon={faQrcode} />
                            </div>
                            <h3 className="step-title">Share Your Link or QR</h3>
                            <p className="step-text">
                                Send via Slack, email, WhatsApp, or display a QR code on slides. Respondents answer
                                on any phone or browser with zero login friction.
                            </p>
                        </div>

                        <div className="step-card">
                            <div className="step-number">03</div>
                            <div className="step-icon-wrap">
                                <FontAwesomeIcon icon={faChartBar} />
                            </div>
                            <h3 className="step-title">Unlock Real Insights</h3>
                            <p className="step-text">
                                Watch 100% anonymous responses aggregate into clear visual graphs. Share a public results
                                URL or download a one-click PDF report.
                            </p>
                            <a
                                href="/f/feedback-JQt6CIr1c"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="step-sample-link"
                                onClick={() => trackEvent('cta_clicked', { location: 'how_it_works_sample_report' })}
                            >
                                See Live Sample Report ↗
                            </a>
                        </div>
                    </div>
                </section>

                {/* 4. WHO USES FEEDBEE (PERSONAS) */}
                <section className="personas-section" id="use-cases">
                    <div className="section-head text-center">
                        <div className="section-tag">TAILORED USE CASES</div>
                        <h2 className="section-title">Built for people who value authentic growth.</h2>
                        <p className="section-desc">
                            Whether you're leading a team, consulting for clients, or speaking on stage, Feedbee gives you the truth.
                        </p>
                    </div>

                    <div className="personas-grid">
                        <div className="persona-card">
                            <div className="persona-badge">Team Leaders & Managers</div>
                            <h3 className="persona-title">360° Peer Reviews & Sprints</h3>
                            <p className="persona-desc">
                                Eliminate the awkwardness of traditional HR reviews. Enable teammates to share
                                constructive suggestions safely and anonymously.
                            </p>
                            <ul className="persona-points">
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Quarterly retrospectives</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Manager & peer feedback</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Team psychological safety</li>
                            </ul>
                        </div>

                        <div className="persona-card">
                            <div className="persona-badge">Freelancers & Agencies</div>
                            <h3 className="persona-title">Client Debriefs & Testimonials</h3>
                            <p className="persona-desc">
                                Send a chic, branded debrief upon project completion. Uncover what your clients
                                loved and collect glowing testimonial ratings.
                            </p>
                            <ul className="persona-points">
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Post-project satisfaction</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Testimonial generation</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Repeat engagement discovery</li>
                            </ul>
                        </div>

                        <div className="persona-card">
                            <div className="persona-badge">Speakers & Educators</div>
                            <h3 className="persona-title">Instant Workshop & Event Polling</h3>
                            <p className="persona-desc">
                                Display your page's QR code on your final presentation slide. Capture attendee ratings
                                and key takeaways before they walk out the door.
                            </p>
                            <ul className="persona-points">
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Real-time audience reactions</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Workshop evaluation scores</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Zero app install for attendees</li>
                            </ul>
                        </div>

                        <div className="persona-card">
                            <div className="persona-badge">Founders & Creators</div>
                            <h3 className="persona-title">Idea & Product Validation</h3>
                            <p className="persona-desc">
                                Test pitch decks, new features, or design prototypes. Get unvarnished critiques from
                                peers and early adopters who won't just say what sounds nice.
                            </p>
                            <ul className="persona-points">
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Unbiased MVP critique</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Feature demand validation</li>
                                <li><FontAwesomeIcon icon={faCheck} className="check-bullet" /> Fast iteration cycles</li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* 5. WHY FEEDBEE (ADVANTAGES) */}
                <section className="advantages-section">
                    <div className="section-head text-center">
                        <div className="section-tag">WHY FEEDBEE</div>
                        <h2 className="section-title">The advantages that set Feedbee apart</h2>
                    </div>

                    <div className="advantages-grid">
                        <div className="advantage-box">
                            <div className="adv-icon">
                                <FontAwesomeIcon icon={faShieldAlt} />
                            </div>
                            <h3>The Politeness Filter Broken</h3>
                            <p>
                                When people answer face-to-face, they sugarcoat. Guaranteed anonymity unlocks the candid,
                                actionable truths you actually need to improve.
                            </p>
                        </div>

                        <div className="advantage-box">
                            <div className="adv-icon">
                                <FontAwesomeIcon icon={faBolt} />
                            </div>
                            <h3>Frictionless For Everyone</h3>
                            <p>
                                No app download, no account required, and no tracking cookies. High completion rates
                                because answering takes less than 60 seconds on any device.
                            </p>
                        </div>

                        <div className="advantage-box">
                            <div className="adv-icon">
                                <FontAwesomeIcon icon={faFilePdf} />
                            </div>
                            <h3>Visual Charts & One-Click PDF</h3>
                            <p>
                                Stop manually tabulating spreadsheet rows. Feedbee generates instant score charts and
                                exports presentation-grade PDF reports automatically.
                            </p>
                        </div>
                    </div>
                </section>

                {/* 6. FAQ SECTION */}
                <section className="faq-section-revamp" id="faq">
                    <div className="section-head text-center">
                        <div className="section-tag">COMMON QUESTIONS</div>
                        <h2 className="section-title">Frequently asked questions</h2>
                        <p className="section-desc">Everything you need to know about collecting feedback with Feedbee.</p>
                    </div>

                    <div className="faq-accordion-wrap">
                        {FAQ_ITEMS.map((item, index) => {
                            const isOpen = this.state.openFaq === index;
                            return (
                                <div
                                    key={index}
                                    className={`faq-item-card ${isOpen ? 'open' : ''}`}
                                    onClick={() => this.setState({ openFaq: isOpen ? -1 : index })}
                                >
                                    <div className="faq-question-row">
                                        <span className="faq-q-text">{item.q}</span>
                                        <FontAwesomeIcon
                                            icon={isOpen ? faChevronUp : faChevronDown}
                                            className="faq-toggle-icon"
                                        />
                                    </div>
                                    {isOpen && <div className="faq-a-text">{item.a}</div>}
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* 7. HIGH-CONVERSION FINAL CALL TO ACTION */}
                <section className="final-cta-section">
                    <div className="final-cta-box">
                        <h2 className="final-cta-title">Ready for feedback that actually helps you grow?</h2>
                        <p className="final-cta-sub">
                            Join team leads, consultants, and creators who use Feedbee to ask the right questions and learn the truth.
                        </p>

                        <div className="final-cta-actions">
                            <Link to="/auth/sign-up" onClick={() => trackEvent('cta_clicked', { location: 'final_cta' })}>
                                <button className="btn-final-cta">
                                    Start using for free <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
                                </button>
                            </Link>
                        </div>

                        <div className="final-cta-meta">
                            <span>✓ Free tier available</span>
                            <span>✓ No credit card required</span>
                            <span>✓ Ready in 2 minutes</span>
                        </div>
                    </div>

                    {/* Integrated Newsletter Footer Widget */}
                    <div className="newsletter-compact-wrap">
                        <div className="newsletter-compact-title">Want product updates & feedback tips?</div>
                        <form onSubmit={this._handleSubmit} className="newsletter-form-inline">
                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={this.state.email}
                                onChange={this._handleChange}
                                className="newsletter-input"
                                required
                            />
                            <button type="submit" className="newsletter-btn">
                                Subscribe
                            </button>
                        </form>
                        <label className="newsletter-legal-note">
                            <input
                                type="checkbox"
                                checked={this.state.checkbox}
                                onChange={this._handleCheckbox}
                                style={{ marginRight: '6px' }}
                            />
                            I agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy-policy">Privacy Policy</Link>.
                        </label>
                        {this.state.emailSubmitted && (
                            <div className="newsletter-msg-success">Thanks for subscribing!</div>
                        )}
                        {this.state.error && (
                            <div className="newsletter-msg-error">{this.state.error}</div>
                        )}
                    </div>
                </section>
            </main>
        );
    }

    _handleChange = (e) => {
        this.setState({ email: e.target.value, error: '' });
    };

    _handleSubmit = (e) => {
        e.preventDefault();
        if (this.state.checkbox === true) {
            let email = { email: this.state.email };
            api.post('/api/emails/add', email)
                .then(() => {
                    this.setState({
                        email: '',
                        error: '',
                        checkbox: false,
                        emailSubmitted: true,
                    });
                    setTimeout(() => {
                        this.setState({ emailSubmitted: false });
                    }, 5000);
                })
                .catch((err) => {
                    this.setState({
                        error: (err && err.description) || 'Something went wrong. Please try again.',
                    });
                });
        } else {
            this.setState({ error: 'Please accept our Terms and Privacy Policy to subscribe.' });
        }
    };

    _handleCheckbox = () => {
        this.setState((prevState) => ({
            checkbox: !prevState.checkbox,
            error: '',
        }));
    };
}

export default Home;
